import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getStoreByOwner, updateStore, deleteStore } from '../lib/firebaseService';
import { AlertTriangle, Trash2, ToggleLeft, ToggleRight, Power, ShieldAlert } from 'lucide-react';

export default function StoreManagement() {
  const { firebaseUser } = useAuth();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  useEffect(() => {
    if (firebaseUser) {
      loadStore();
    }
  }, [firebaseUser]);

  const loadStore = async () => {
    try {
      setPageLoading(true);
      const userStore = await getStoreByOwner(firebaseUser.uid);
      setStore(userStore);
    } catch (error) {
      console.error('Mağaza yüklənə bilmədi:', error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!store) return;

    const newStatus = !store.is_active;
    const action = newStatus ? 'aktivləşdirmək' : 'deaktiv etmək';
    
    if (!window.confirm(`Mağazanı ${action} istədiyinizə əminsiniz?`)) return;

    setDeactivateLoading(true);
    try {
      await updateStore(store.id, { is_active: newStatus });
      toast.success(`Mağaza ${newStatus ? 'aktivləşdirildi' : 'deaktiv edildi'}`);
      loadStore();
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleDeleteStore = async () => {
    if (!store) return;

    setDeleteLoading(true);
    try {
      await deleteStore(store.id);
      toast.success('Mağaza silindi');
      setTimeout(() => {
        navigate('/dashboard');
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Silmə xətası:', error);
      toast.error('Mağaza silinə bilmədi');
      setDeleteLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!store) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl border border-border p-8 text-center">
            <p className="text-muted-foreground mb-4">Əvvəlcə mağaza yaradın</p>
            <Button onClick={() => window.location.href = '/dashboard/store/info'}>
              Mağaza Yarat
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-8 h-8 text-orange-600" />
            Mağaza İdarəetməsi
          </h1>
          <p className="text-muted-foreground mt-1">Mağazanızın statusunu idarə edin</p>
        </div>

        <div className="space-y-6">
          {/* Mağaza Statusu */}
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                store.is_active ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {store.is_active ? (
                  <ToggleRight className="w-6 h-6 text-green-600" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-gray-600" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-1">
                  Mağaza Statusu: {store.is_active ? (
                    <span className="text-green-600">Aktiv</span>
                  ) : (
                    <span className="text-gray-600">Deaktiv</span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {store.is_active ? (
                    'Mağazanız hazırda aktiv və müştərilər tərəfindən görünə bilər.'
                  ) : (
                    'Mağazanız deaktivdir. Müştərilər mağazanıza daxil ola bilməz.'
                  )}
                </p>
                <Button
                  variant={store.is_active ? 'outline' : 'default'}
                  onClick={handleToggleActive}
                  disabled={deactivateLoading}
                  className={store.is_active ? 'text-orange-600 border-orange-600 hover:bg-orange-50' : ''}
                >
                  {deactivateLoading ? 'Yüklənir...' : (
                    <>
                      <Power className="w-4 h-4 mr-2" />
                      {store.is_active ? 'Mağazanı Deaktiv Et' : 'Mağazanı Aktivləşdir'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Mağaza Məlumatları */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2 text-blue-900">Mağaza Məlumatları</h4>
            <div className="space-y-1 text-sm text-blue-800">
              <p><strong>Ad:</strong> {store.store_name}</p>
              <p><strong>Subdomain:</strong> {store.subdomain ? `${store.subdomain}.iveb.az` : 'Təyin edilməyib'}</p>
              <p><strong>WhatsApp:</strong> {store.whatsapp_number}</p>
            </div>
          </div>

          {/* Təhlükə Zonası */}
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-1 text-red-900">Təhlükə Zonası</h3>
                <p className="text-sm text-red-800 mb-4">
                  Mağazanızı silmək qeyri-dönəndir. Bütün məhsullar, sifarişlər və məlumatlar silinəcək.
                </p>
                
                {!showDeleteConfirm ? (
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Mağazanı Sil
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                      <p className="text-sm font-semibold text-red-900 mb-2">
                        ⚠️ Bu əməliyyat qeyri-dönəndir!
                      </p>
                      <p className="text-xs text-red-800 mb-3">
                        Mağazanız, bütün məhsullar, sifarişlər və özəlləşdirmələr tamamilə silinəcək. Bu əməliyyatı geri qaytarmaq mümkün deyil.
                      </p>
                      <p className="text-xs text-red-800 font-semibold">
                        Davam etmək istədiyinizə əminsinizmi?
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="flex-1"
                      >
                        Ləğv et
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDeleteStore}
                        disabled={deleteLoading}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                      >
                        {deleteLoading ? 'Silinir...' : 'Bəli, Sil'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Məlumat */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Qeyd:</strong> Mağazanızı deaktiv etsəniz, müştərilər mağazanıza daxil ola bilməzlər, amma məlumatlarınız saxlanılır və istədiyiniz zaman yenidən aktivləşdirə bilərsiniz.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
